/**
 * LES NIVEAUX ET LA MÉMOIRE (DEV-1508 lot 2, engagements E6 et E8).
 * ============================================================================
 *
 * INTENTION : on regarde une fenêtre, on la resserre, on revient. La page doit
 * suivre sans jamais redemander ce qu'elle a déjà. Trois règles, dans cet ordre.
 *
 * 1. LE NIVEAU SUIT LA FENÊTRE REGARDÉE, il ne se choisit pas : courbe native
 *    jusqu'à 14 jours, enveloppe horaire de 15 à 92, enveloppe journalière
 *    au-delà (spec v1.5 §4, validée le 09/09). Pas de marge anti-oscillation :
 *    le brush est un geste discret, il n'y a rien à lisser.
 *
 * 2. ON CHARGE LA FENÊTRE REGARDÉE ÉLARGIE D'UN QUART de chaque côté, bornée par
 *    le contexte : un déplacement de la main ne redéclenche pas un appel.
 *
 * 3. LA MÉMOIRE EST PAR ONGLET × DIRECTION × NIVEAU, jamais par variable. Une
 *    fenêtre déjà couverte à ce niveau ne coûte RIEN, et revenir au contexte ne
 *    coûte rien non plus (le contexte est toujours chargé). Métrique A6 : au
 *    moins 60 % des recadrages sans appel.
 *
 *    Couvrir les DATES ne suffit pourtant pas : une réponse dont une statistique
 *    a été OMISE sous un plafond de calcul ne dit rien de juste sur une fenêtre
 *    plus courte que ce plafond, qui serait servie si on la demandait seule. Un
 *    tel bloc ne couvre pas, on redemande UNE fois, et la réponse remplace.
 *
 * Les plafonds du lecteur sont respectés AVANT l'appel (E8) : le natif ne part
 * jamais sur plus de 31 jours d'un coup, l'enveloppe horaire jamais au-delà des
 * 31 jours où elle se dérive du natif, une demande ne porte jamais plus de douze
 * variables, la puissance apparente n'est demandée qu'aux grains natif et
 * horaire, en moyenne seulement. Un 400 servi à l'écran serait notre faute, et
 * une enveloppe qu'on s'est fait refuser en découpant trop large aussi.
 *
 * Et ce que le lecteur ne REFUSE pas se règle ici aussi : au natif, l'enveloppe
 * minimum et maximum n'est pas demandée, parce qu'elle reviendrait en séries
 * entièrement nulles là où chaque point est déjà une mesure.
 *
 * ⚠️ Ce que ce module ne fait JAMAIS : additionner soutirage et injection (E5 :
 * deux jeux de séries, un appel chacun), et remplacer un `null` par un 0.
 */
import { computed, ref } from "vue"
import {
    fetchSeries,
    plafondDerivationJours,
    plafondJours,
    GRAINS_APPARENTE,
    PLAFOND_VARIABLES,
    type DemandeSeries,
    type DirectionEnedis,
    type GrainSeries,
    type MetaSeries,
    type PointX,
    type ProvenanceSeries,
    type ReponseSeries,
    type SerieEnedis,
    type StatSeries,
} from "@modules/data/services/sourceDataEnedis.service"

// ============================================================================
// LES NIVEAUX
// ============================================================================

/**
 * Le grain auquel un onglet est SERVI.
 *
 * La LARGEUR regardée en propose trois (natif, horaire, journalier) ; ce que le
 * compteur DÉTIENT peut n'en offrir aucun des trois — les périodes de
 * facturation ne vivent qu'au grain `periode`, les index qu'au jour. Le grain
 * retenu est donc le plus fin que ce compteur détienne à partir du niveau de la
 * fenêtre (`grainRetenu`), et non le niveau tout seul : demander une période de
 * facturation « au jour » rend des colonnes vides sur un compteur qui a tout.
 */
export type NiveauSeries = GrainSeries

/** Jusqu'à 14 jours regardés : la courbe native. */
export const SEUIL_NATIF_JOURS = 14
/** De 15 à 92 jours : l'enveloppe horaire. Au-delà : l'enveloppe journalière. */
export const SEUIL_HORAIRE_JOURS = 92

/** Les grains du lecteur, du plus fin au plus grossier. */
const ORDRE_GRAINS: GrainSeries[] = [
    "natif",
    "hourly",
    "daily",
    "weekly",
    "monthly",
    "periode",
]

/**
 * Le grain réellement demandé : le plus fin que ce compteur DÉTIENNE, à partir
 * du niveau que la fenêtre impose.
 *
 * Sans grain détenu connu, le niveau passe tel quel (comportement du lot 2).
 * Quand aucun grain détenu n'est aussi grossier que le niveau, le plus grossier
 * des grains détenus est retenu : mieux vaut la seule résolution qui existe
 * qu'une série de `null` sous un grain que ce compteur n'a jamais eu.
 */
export function grainRetenu(
    niveau: NiveauSeries,
    grains: GrainSeries[]
): NiveauSeries {
    if (grains.length === 0) return niveau
    if (grains.includes(niveau)) return niveau
    const depart = ORDRE_GRAINS.indexOf(niveau)
    const candidats = ORDRE_GRAINS.filter((g) => grains.includes(g))
    return (
        candidats.find((g) => ORDRE_GRAINS.indexOf(g) >= depart) ??
        candidats[candidats.length - 1]
    )
}

/** Le niveau que la LARGEUR de la fenêtre regardée impose. */
export function niveauPourFenetre(jours: number): NiveauSeries {
    if (jours <= SEUIL_NATIF_JOURS) return "natif"
    if (jours <= SEUIL_HORAIRE_JOURS) return "hourly"
    return "daily"
}

// ============================================================================
// LES FENÊTRES — `YYYY-MM-DD`, bornes INCLUSES des deux côtés.
// ============================================================================

export type Fenetre = { debut: string; fin: string }

const MS_PAR_JOUR = 86_400_000

function versUtc(jour: string): number {
    const [a, m, j] = jour.split("-").map(Number)
    return Date.UTC(a, (m ?? 1) - 1, j ?? 1)
}

function versJour(ms: number): string {
    return new Date(ms).toISOString().slice(0, 10)
}

/** Nombre de jours d'une fenêtre, bornes incluses (un jour seul en vaut 1). */
export function joursDeFenetre(fenetre: Fenetre): number {
    return (
        Math.floor(
            (versUtc(fenetre.fin) - versUtc(fenetre.debut)) / MS_PAR_JOUR
        ) + 1
    )
}

/** Décale un jour de `n` jours (n négatif accepté). */
export function decaleJour(jour: string, n: number): string {
    return versJour(versUtc(jour) + n * MS_PAR_JOUR)
}

/** Vrai quand `contenante` couvre entièrement `fenetre`. */
export function couvre(contenante: Fenetre, fenetre: Fenetre): boolean {
    return (
        versUtc(contenante.debut) <= versUtc(fenetre.debut) &&
        versUtc(contenante.fin) >= versUtc(fenetre.fin)
    )
}

/**
 * La fenêtre regardée élargie d'un QUART de sa largeur de chaque côté, bornée
 * par le contexte. C'est ce qu'on charge, pas ce qu'on regarde.
 */
export function elargirDunQuart(fenetre: Fenetre, contexte: Fenetre): Fenetre {
    const quart = Math.max(1, Math.ceil(joursDeFenetre(fenetre) / 4))
    const debut = Math.max(
        versUtc(contexte.debut),
        versUtc(fenetre.debut) - quart * MS_PAR_JOUR
    )
    const fin = Math.min(
        versUtc(contexte.fin),
        versUtc(fenetre.fin) + quart * MS_PAR_JOUR
    )
    return { debut: versJour(debut), fin: versJour(fin) }
}

/**
 * Découpe une fenêtre en appels qui tiennent sous les plafonds du lecteur.
 *
 * DEUX plafonds, pas un, et c'est là que le défaut du 11/09 se tenait.
 *
 *  • le plafond de LECTURE du grain : le natif ne se relit jamais sur plus de
 *    31 jours d'un coup ;
 *  • le plafond de DÉRIVATION des statistiques d'enveloppe : à l'heure, le
 *    minimum et le maximum sont recalculés depuis la courbe native, et ce calcul
 *    ne se fait que sur 31 jours. Au-delà, le lecteur OMET les deux statistiques
 *    et la page écrivait « non servie sur cette fenêtre » — alors que la fenêtre
 *    REGARDÉE (31 jours) y avait droit et que seul l'élargissement d'un quart
 *    l'en avait sortie. Le maximum des puissances moyennes n'apparaissait donc
 *    jamais.
 *
 * Le plus petit des deux gagne. Les morceaux sont consécutifs, sans recouvrement
 * ni jour perdu, et se recollent ensuite par `concatenerMorceaux`.
 */
export function decoupePourNiveau(
    fenetre: Fenetre,
    niveau: NiveauSeries,
    variables: string[],
    stats: StatSeries[] = []
): Fenetre[] {
    const plafond = plusPetitPlafond(
        plafondJours(niveau, variables),
        plafondDerivationJours(niveau, stats)
    )
    if (plafond === null || joursDeFenetre(fenetre) <= plafond) return [fenetre]
    const morceaux: Fenetre[] = []
    let debut = versUtc(fenetre.debut)
    const fin = versUtc(fenetre.fin)
    while (debut <= fin) {
        const borne = Math.min(fin, debut + (plafond - 1) * MS_PAR_JOUR)
        morceaux.push({ debut: versJour(debut), fin: versJour(borne) })
        debut = borne + MS_PAR_JOUR
    }
    return morceaux
}

/** Le plus petit de deux plafonds, `null` valant « aucun plafond de ce côté ». */
function plusPetitPlafond(a: number | null, b: number | null): number | null {
    if (a === null) return b
    if (b === null) return a
    return Math.min(a, b)
}

// ============================================================================
// RECOLLER LES MORCEAUX — une fenêtre découpée redevient UNE réponse
// ============================================================================

/** Colonnes de `meta` alignées sur `x`, par `variable.stat`. */
const COLONNES_ALIGNEES = ["at", "nature", "n", "partiel"] as const

/** Cartes de `meta` indexées par `variable.stat`, sans alignement sur `x`. */
const CARTES_META = [
    "unites",
    "natures",
    "calcule_a_la_lecture",
    "plafond_applique",
    "pas_natif_par_jour",
] as const

function cleSerie(variable: string, stat: StatSeries): string {
    return `${variable}.${stat}`
}

/**
 * Recolle en UNE réponse les morceaux consécutifs d'une même fenêtre découpée.
 *
 * Sans cela, une fenêtre horaire de quarante jours découpée en deux appels
 * rendait DEUX séries portant la même clé : deux courbes superposées dans la
 * légende, chacune sur sa moitié de l'axe. Le découpage est une contrainte du
 * lecteur, pas une information pour le client : ce qu'il regarde est une courbe.
 *
 * Ce qui est recollé, et comment :
 *  • `x`, `v`, `t` se concatènent dans l'ordre des fenêtres, sans tri ni
 *    déduplication : les morceaux ne se recouvrent pas par construction ;
 *  • les colonnes de `meta` alignées sur `x` se concatènent DE LA MÊME longueur
 *    que les points du morceau, comblées à `null` quand un morceau ne les porte
 *    pas. Une colonne plus courte que `x` dirait « Mesuré » sur des points
 *    calculés ;
 *  • `plafond_applique` est une UNION : une statistique plafonnée sur un seul
 *    morceau reste dite. `seriesPlafonnees` n'en fait une mention que si aucun
 *    morceau ne l'a servie.
 *
 * Les réponses de directions ou de grains différents ne se mélangent jamais.
 */
export function concatenerMorceaux(reponses: ReponseSeries[]): ReponseSeries[] {
    const groupes = new Map<string, ReponseSeries[]>()
    for (const reponse of reponses) {
        const cle = `${reponse?.meta?.direction ?? ""}|${reponse?.meta?.grain ?? ""}`
        groupes.set(cle, [...(groupes.get(cle) ?? []), reponse])
    }
    return [...groupes.values()].map((morceaux) =>
        morceaux.length > 1 ? recoller(morceaux) : morceaux[0]
    )
}

function recoller(morceaux: ReponseSeries[]): ReponseSeries {
    const ordonnes = [...morceaux].sort((a, b) =>
        (a?.meta?.date_from ?? "") < (b?.meta?.date_from ?? "") ? -1 : 1
    )

    // 1. Les séries, par `variable.stat`, dans l'ordre de première apparition.
    const series = new Map<string, SerieEnedis>()
    for (const morceau of ordonnes) {
        for (const serie of morceau?.data ?? []) {
            const cle = cleSerie(serie.variable, serie.stat)
            const deja = series.get(cle)
            if (!deja) {
                series.set(cle, {
                    ...serie,
                    x: [...(serie.x ?? [])],
                    v: [...(serie.v ?? [])],
                    t: [...(serie.t ?? [])],
                })
                continue
            }
            deja.x = [...deja.x, ...((serie.x ?? []) as PointX[])]
            deja.v = [...deja.v, ...(serie.v ?? [])]
            deja.t = [
                ...deja.t,
                ...((serie.t ?? []) as Array<ProvenanceSeries | null>),
            ]
        }
    }

    // 2. Les cartes de `meta` : une union, le dernier morceau l'emporte sur une
    //    clé vue deux fois (il n'y a rien à arbitrer, elles disent la même chose).
    const meta: MetaSeries = { ...ordonnes[0].meta }
    for (const carte of CARTES_META) {
        const fusion: Record<string, unknown> = {}
        let vue = false
        for (const morceau of ordonnes) {
            const valeur = morceau?.meta?.[carte]
            if (!valeur) continue
            vue = true
            Object.assign(fusion, valeur)
        }
        if (vue) {
            ;(meta as unknown as Record<string, unknown>)[carte] = fusion
        }
    }

    // 3. Les colonnes alignées sur `x` : concaténées morceau par morceau, à la
    //    longueur des points de CE morceau, comblées à `null` quand il se tait.
    for (const colonne of COLONNES_ALIGNEES) {
        const cles = new Set<string>()
        for (const morceau of ordonnes) {
            for (const cle of Object.keys(morceau?.meta?.[colonne] ?? {})) {
                cles.add(cle)
            }
        }
        if (cles.size === 0) continue
        const fusion: Record<string, unknown[]> = {}
        for (const cle of cles) {
            const valeurs: unknown[] = []
            for (const morceau of ordonnes) {
                const points =
                    (morceau?.data ?? []).find(
                        (serie) => cleSerie(serie.variable, serie.stat) === cle
                    )?.x?.length ?? 0
                const servie = (
                    morceau?.meta?.[colonne] as
                        | Record<string, unknown[]>
                        | undefined
                )?.[cle]
                valeurs.push(
                    ...(Array.isArray(servie)
                        ? servie
                        : Array.from({ length: points }, () => null))
                )
            }
            fusion[cle] = valeurs
        }
        ;(meta as unknown as Record<string, unknown>)[colonne] = fusion
    }

    meta.date_from = ordonnes[0]?.meta?.date_from
    meta.date_to = ordonnes[ordonnes.length - 1]?.meta?.date_to
    if (meta.date_from && meta.date_to) {
        meta.fenetre_jours = joursDeFenetre({
            debut: meta.date_from,
            fin: meta.date_to,
        })
    }

    // La qualité d'une variable ne dépend pas du morceau : le premier fait qui la
    // dit est gardé, aucun n'est écrasé par un plus tardif.
    const qualite: ReponseSeries["qualite"] = {}
    for (const morceau of ordonnes) {
        for (const [variable, fait] of Object.entries(morceau?.qualite ?? {})) {
            if (!(variable in qualite)) qualite[variable] = fait
        }
    }

    return { meta, data: [...series.values()], qualite }
}

// ============================================================================
// LES VARIABLES — plafonds et grains admis
// ============================================================================

/** Lots de douze variables au plus : le plafond du lecteur, jamais dépassé. */
export function lotsDeVariables(
    variables: string[],
    taille: number = PLAFOND_VARIABLES
): string[][] {
    const lots: string[][] = []
    for (let i = 0; i < variables.length; i += taille) {
        lots.push(variables.slice(i, i + taille))
    }
    return lots
}

/** Vrai pour la puissance apparente, servie aux seuls grains fins. */
export function estApparente(variable: string): boolean {
    return variable.startsWith("puissanceapparente")
}

/**
 * Les variables demandables à ce niveau. La puissance apparente est dérivée
 * point à point : elle n'est juste que sur des points mesurés ensemble, donc
 * servie aux grains natif et horaire seulement. Au journalier, elle est ÉCARTÉE
 * de la demande, jamais demandée pour rien.
 */
export function variablesAdmises(
    variables: string[],
    niveau: NiveauSeries
): { retenues: string[]; ecartees: string[] } {
    const apparenteAdmise = GRAINS_APPARENTE.includes(niveau)
    const retenues: string[] = []
    const ecartees: string[] = []
    for (const variable of variables) {
        if (!apparenteAdmise && estApparente(variable)) ecartees.push(variable)
        else retenues.push(variable)
    }
    return { retenues, ecartees }
}

/**
 * Les statistiques d'ENVELOPPE : un minimum et un maximum sur un agrégat.
 *
 * ⚠️ Au grain natif, chaque point EST une mesure : il n'y a rien à envelopper,
 * et le lecteur ne sert pas ces deux statistiques. Demandées quand même, elles
 * ne sont pas refusées : elles reviennent en séries entièrement nulles (relevé
 * du 10/09 sur un compteur témoin : 720 points, 716 valeurs sur la moyenne,
 * 720 `null` sur le minimum comme sur le maximum). La page les tracerait comme
 * des trous alors qu'il ne manque rien. C'est au front de ne pas demander.
 */
export const STATS_ENVELOPPE: StatSeries[] = ["min", "max"]

/**
 * Les statistiques demandables à ce NIVEAU. L'enveloppe part à l'heure et au
 * jour, jamais au natif. Le total de l'énergie et le dernier relevé des index
 * passent inchangés : ils ne sont pas une enveloppe.
 *
 * La demande n'est jamais vidée : si l'enveloppe était la seule statistique
 * voulue, elle part telle quelle plutôt que de partir sans aucune statistique,
 * ce que le lecteur refuserait.
 */
export function statsPourNiveau(
    stats: StatSeries[],
    niveau: NiveauSeries
): StatSeries[] {
    if (niveau !== "natif") return stats
    const retenues = stats.filter((s) => !STATS_ENVELOPPE.includes(s))
    return retenues.length > 0 ? retenues : stats
}

/** La puissance apparente n'a qu'une statistique : la moyenne. */
export function statsAdmises(
    variables: string[],
    stats: StatSeries[]
): StatSeries[] {
    if (variables.length > 0 && variables.every(estApparente)) {
        return stats.filter((s) => s === "mean")
    }
    return stats
}

/**
 * Range les variables en lots HOMOGÈNES : l'apparente d'un côté, le reste de
 * l'autre.
 *
 * ⚠️ Sans cette séparation, une demande qui mêle la puissance active (moyenne,
 * minimum, maximum) et l'apparente (moyenne seule) partirait avec un `min` sur
 * l'apparente, que le lecteur ne sert pas. Le stat par appel vaut pour TOUTES
 * les variables de l'appel : c'est la composition du lot qui décide, pas une
 * règle par variable. Deux appels valent mieux qu'un refus.
 */
export function lotsHomogenes(variables: string[]): string[][] {
    const apparentes = variables.filter(estApparente)
    const autres = variables.filter((v) => !estApparente(v))
    return [...lotsDeVariables(autres), ...lotsDeVariables(apparentes)]
}

// ============================================================================
// LA MÉMOIRE — par onglet × direction × niveau
// ============================================================================

/** Ce qu'un chargement a rapporté, avec la fenêtre qu'il couvre. */
export type BlocMemoire = {
    fenetre: Fenetre
    reponses: ReponseSeries[]
    /**
     * Vrai quand ce bloc est DÉJÀ le fruit d'une redemande sous plafond. On ne
     * redemande qu'une fois : si le lecteur plafonne encore, c'est sa réponse,
     * et elle se sert telle quelle plutôt que de repartir à chaque geste.
     */
    redemande?: boolean
}

export type MemoireSeries = {
    /** Clé de rangement : onglet × direction × niveau, jamais la variable. */
    cle: (
        onglet: string,
        direction: DirectionEnedis,
        niveau: NiveauSeries
    ) => string
    /** Le bloc qui couvre VRAIMENT cette fenêtre à ce niveau, ou `null`. */
    lire: (cle: string, fenetre: Fenetre) => BlocMemoire | null
    /**
     * Vrai quand un bloc couvre bien la fenêtre mais qu'une statistique y a été
     * omise sous un plafond que cette fenêtre-ci ne dépasse pas.
     */
    plafonneSous: (cle: string, fenetre: Fenetre) => boolean
    retenir: (cle: string, bloc: BlocMemoire) => void
    /** Nombre de blocs retenus, pour les tests et les métriques. */
    taille: () => number
    vider: () => void
}

/**
 * Le plus petit plafond de dérivation porté par un bloc, ou `null`.
 *
 * Une statistique servie par le lecteur ne compte pas, même si `plafond_applique`
 * la nomme : ce qui est tracé se lit. Un motif sans `plafond_jours` (un pas natif
 * trop grossier, une statistique hors nomenclature) ne compte pas non plus :
 * redemander une fenêtre plus courte n'y changerait rien.
 */
export function plafondDuBloc(reponses: ReponseSeries[]): number | null {
    let plafond: number | null = null
    for (const reponse of reponses) {
        const servies = new Set(
            (reponse?.data ?? []).map(
                (serie) => `${serie.variable}.${serie.stat}`
            )
        )
        for (const [cle, motif] of Object.entries(
            reponse?.meta?.plafond_applique ?? {}
        )) {
            if (servies.has(cle)) continue
            const jours = motif?.plafond_jours
            if (typeof jours !== "number" || !Number.isFinite(jours)) continue
            plafond = plafond === null ? jours : Math.min(plafond, jours)
        }
    }
    return plafond
}

/**
 * Ce bloc couvre-t-il VRAIMENT cette fenêtre ?
 *
 * Couvrir les dates ne suffit pas. Une réponse obtenue sur 88 jours, dont
 * l'enveloppe a été omise parce que son calcul s'arrête à 31, ne dit rien de
 * juste sur les 15 jours qu'on regarde maintenant : la même fenêtre, redemandée
 * seule, serait servie. La resservir depuis la mémoire, c'est afficher « non
 * servie sur cette fenêtre » sur une fenêtre qui y a droit, sans un appel et
 * sans un moyen de s'en sortir (constat du 11/09 sur staging).
 */
export function blocCouvreVraiment(
    bloc: BlocMemoire,
    fenetre: Fenetre
): boolean {
    if (bloc.redemande) return true
    const plafond = plafondDuBloc(bloc.reponses)
    return plafond === null || joursDeFenetre(fenetre) > plafond
}

export function creerMemoire(): MemoireSeries {
    const blocs = new Map<string, BlocMemoire[]>()
    function couvrants(cle: string, fenetre: Fenetre): BlocMemoire[] {
        return (blocs.get(cle) ?? []).filter((bloc) =>
            couvre(bloc.fenetre, fenetre)
        )
    }
    return {
        cle: (onglet, direction, niveau) => `${onglet}|${direction}|${niveau}`,
        lire(cle, fenetre) {
            return (
                couvrants(cle, fenetre).find((bloc) =>
                    blocCouvreVraiment(bloc, fenetre)
                ) ?? null
            )
        },
        plafonneSous(cle, fenetre) {
            const candidats = couvrants(cle, fenetre)
            return (
                candidats.length > 0 &&
                !candidats.some((bloc) => blocCouvreVraiment(bloc, fenetre))
            )
        },
        retenir(cle, bloc) {
            const existants = blocs.get(cle) ?? []
            // Un bloc déjà contenu dans un autre n'ajoute rien ; un bloc qui en
            // contient d'anciens les remplace. La mémoire ne grossit pas d'un
            // zoom à l'autre.
            //
            // DEUX exceptions, et elles vont dans le même sens : une redemande
            // sous plafond porte une statistique que le bloc contenant n'a PAS,
            // donc ni elle ne se jette parce qu'un plus large la contient, ni un
            // plus large lui-même plafonné ne la remplace.
            const gardes = existants.filter(
                (a) =>
                    !couvre(bloc.fenetre, a.fenetre) ||
                    (a.redemande === true &&
                        plafondDuBloc(bloc.reponses) !== null)
            )
            if (
                bloc.redemande !== true &&
                gardes.some((a) => couvre(a.fenetre, bloc.fenetre))
            ) {
                blocs.set(cle, gardes)
                return
            }
            blocs.set(cle, [...gardes, bloc])
        },
        taille() {
            let n = 0
            for (const liste of blocs.values()) n += liste.length
            return n
        },
        vider() {
            blocs.clear()
        },
    }
}

// ============================================================================
// LE COMPOSABLE
// ============================================================================

export type DemandeOnglet = {
    idSource: string
    /** Identifiant de l'onglet : la mémoire est rangée par lui. */
    onglet: string
    /** Une entrée par direction regardée. Jamais sommées (E5). */
    directions: DirectionEnedis[]
    /**
     * Les variables dataV2 à demander. Un tableau vaut pour tous les sens ; un
     * dictionnaire donne à CHAQUE sens les siennes (lot 5g).
     *
     * ⚠️ POURQUOI LE DICTIONNAIRE EXISTE. Les appelants passaient l'UNION des
     * variables des sens regardés, et cette union partait telle quelle pour
     * chaque sens : sur un compteur à deux sens, l'appel `direction=soutirage`
     * portait les noms `…_elecinjection_…` et réciproquement. Le lecteur rend
     * alors une erreur, la page entière tombe dans son `catch`, et le client
     * voit « aucune mesure sur cette fenêtre » plus une bande figée en
     * squelette (relevé en prod le 17/09 sur quatre compteurs à deux sens).
     * Un sens de plus coûte SES appels, jamais ceux de l'autre.
     */
    variables: string[] | Partial<Record<DirectionEnedis, string[]>>
    stats: StatSeries[] | Partial<Record<DirectionEnedis, StatSeries[]>>
    /** La période chargée, plafonnée par le dû. Elle borne tout. */
    contexte: Fenetre
    /** La fenêtre regardée. Elle décide du niveau. */
    fenetre: Fenetre
    /**
     * Les grains où ce compteur DÉTIENT les variables de l'onglet. Le grain
     * demandé s'y accorde : un onglet qui ne vit qu'au grain `periode` n'est
     * jamais demandé au jour.
     */
    grains?: GrainSeries[]
}

/**
 * Ce qui est demandé POUR UN SENS : le tableau tel quel, ou l'entrée de ce sens
 * dans le dictionnaire. Un sens absent du dictionnaire ne demande RIEN — c'est
 * exactement ce qu'il faut faire d'un sens qui ne sert aucune variable.
 */
export function pourLeSens<T>(
    demande: T[] | Partial<Record<DirectionEnedis, T[]>>,
    direction: DirectionEnedis
): T[] {
    return Array.isArray(demande) ? demande : (demande[direction] ?? [])
}

export type ResultatChargement = {
    niveau: NiveauSeries
    /** Fenêtre réellement demandée (regardée, élargie d'un quart, bornée). */
    fenetreChargee: Fenetre
    /** Nombre d'appels réellement partis. Zéro quand tout était en mémoire. */
    appels: number
    /** Vrai quand AUCUN appel n'est parti (métrique A6). */
    depuisMemoire: boolean
    /** Réponses par direction, dans l'ordre de `directions`. */
    parDirection: Array<{
        direction: DirectionEnedis
        reponses: ReponseSeries[]
    }>
    /** Variables écartées par un grain, dites plutôt que tues. */
    ecartees: string[]
}

/** Le lecteur injecté (le vrai service par défaut ; un double dans les tests). */
export type LecteurSeries = (
    idSource: string,
    demande: DemandeSeries,
    signal?: AbortSignal
) => Promise<ReponseSeries>

export function useSeriesEnedis(lecteur: LecteurSeries = fetchSeries) {
    const memoire = creerMemoire()
    // COMBIEN de chargements sont en vol, pas « est-ce qu'il y en a un ».
    // Deux peuvent se chevaucher — on resserre, on revient avant la fin — et un
    // booléen posé à `false` par le premier qui finit annonçait « c'est chargé »
    // pendant que l'autre travaillait encore.
    const chargementsEnVol = ref(0)
    const enCours = computed(() => chargementsEnVol.value > 0)

    /**
     * Charge ce qu'il manque pour peindre `fenetre` dans `onglet`, et RIEN de
     * plus. Ce qui est déjà en mémoire à ce niveau ne repart jamais.
     */
    async function charger(
        demande: DemandeOnglet,
        signal?: AbortSignal,
        /**
         * Vrai quand un geste plus récent est parti : le chargement s'arrête
         * entre deux morceaux.
         *
         * Une fenêtre découpée coûte plusieurs appels, et rien n'obligeait les
         * suivants à partir une fois que personne ne regarde plus cette fenêtre.
         * Un aller-retour rapide en payait la note entière. On s'arrête PROPREMENT
         * (jamais au milieu d'un appel déjà parti) et on ne retient rien : un bloc
         * amputé de ses derniers jours se relirait ensuite comme un trou.
         */
        perime?: () => boolean
    ): Promise<ResultatChargement> {
        const niveau = grainRetenu(
            niveauPourFenetre(joursDeFenetre(demande.fenetre)),
            demande.grains ?? []
        )
        const fenetreChargee = elargirDunQuart(
            demande.fenetre,
            demande.contexte
        )

        let appels = 0
        let interrompu = false
        const parDirection: ResultatChargement["parDirection"] = []
        const ecartees = new Set<string>()
        chargementsEnVol.value += 1
        try {
            for (const direction of demande.directions) {
                if (interrompu) break
                // CHAQUE SENS DEMANDE LES SIENNES, et rien de l'autre (5g).
                const admises = variablesAdmises(
                    pourLeSens(demande.variables, direction),
                    niveau
                )
                const retenues = admises.retenues
                for (const variable of admises.ecartees) ecartees.add(variable)
                const statsDuSens = pourLeSens(demande.stats, direction)
                const cle = memoire.cle(demande.onglet, direction, niveau)
                const dejaLa = memoire.lire(cle, demande.fenetre)
                if (dejaLa) {
                    parDirection.push({ direction, reponses: dejaLa.reponses })
                    continue
                }
                // Une réponse plafonnée déjà en mémoire ne se ressert pas sur
                // une fenêtre plus courte : on redemande, UNE fois.
                const redemande = memoire.plafonneSous(cle, demande.fenetre)
                const reponses: ReponseSeries[] = []
                for (const lot of lotsHomogenes(retenues)) {
                    const stats = statsAdmises(
                        lot,
                        statsPourNiveau(statsDuSens, niveau)
                    )
                    const morceaux: ReponseSeries[] = []
                    for (const morceau of decoupePourNiveau(
                        fenetreChargee,
                        niveau,
                        lot,
                        stats
                    )) {
                        if (appels > 0 && perime?.()) {
                            interrompu = true
                            break
                        }
                        morceaux.push(
                            await lecteur(
                                demande.idSource,
                                {
                                    direction,
                                    variables: lot,
                                    grain: niveau,
                                    stats,
                                    dateFrom: morceau.debut,
                                    dateTo: morceau.fin,
                                },
                                signal
                            )
                        )
                        appels += 1
                    }
                    // Le découpage est une contrainte du lecteur : ce qui remonte
                    // à l'écran est UNE courbe par série, pas une par morceau.
                    reponses.push(...concatenerMorceaux(morceaux))
                    if (interrompu) break
                }
                // Un chargement arrêté en route ne se retient PAS : il lui manque
                // des jours, et la mémoire les servirait ensuite comme un trou.
                if (!interrompu) {
                    memoire.retenir(cle, {
                        fenetre: fenetreChargee,
                        reponses,
                        redemande,
                    })
                }
                parDirection.push({ direction, reponses })
            }
        } finally {
            chargementsEnVol.value -= 1
        }

        return {
            niveau,
            fenetreChargee,
            appels,
            depuisMemoire: appels === 0,
            parDirection,
            ecartees: [...ecartees],
        }
    }

    return { charger, memoire, enCours }
}
